import React, { Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";

// Third party
import * as Yup from "yup";

// Material
import { Box, Chip, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";

// Styling
import flexStyles from "../../../../style/flex";

// Utils
import useValidator from "../../../../utils/useValidator";

// Components
import PrimaryButton from "../../../../Components/UI/Buttons/PrimaryButton";
import SecondaryButton from "../../../../Components/UI/Buttons/SecondaryButton";
import CustomCard from "../../../../Components/UI/CustomCard";
import FormLayout from "../../../../Components/UI/FormLayout";
import SkeletonLoader from "../../../../Components/UI/SkeletonLoader";
import {
  addHubBlog,
  editHubBlog,
  getViewData,
} from "../../../../Stores/HubBlog/actions";
import DocumentModal from "../DocumentModal";

const HubAdd = () => {
  const dispatch = useDispatch();

  const { id } = useParams();
  const history = useHistory();
  const isEditing = useRouteMatch(`/d/company/hub/edit/:id`)?.isExact;

  const flexClasses = flexStyles();

  const { activeLocation } = useSelector(
    (state) => state.SlickCareProfilesReducer
  );
  const userInfo = useSelector((state) => state.AuthReducer.user);

  const { isFetching, currentHubBlog, isUpdating, isFetchingData } =
    useSelector((state) => state.HubBlogReducer);

  let loading = isFetching || isUpdating || isFetchingData;

  const [tagsAutoComplete, setTagsAutoComplete] = useState([]);
  const [docModal, setDocModal] = useState({
    open: false,
  });

  function onSubmit() {
    let data = {
      ...values,
      tags: values?.tags?.toString(),
      company_id: userInfo?.company_id,
    };
    if (isEditing) {
      dispatch(editHubBlog({ data, id: currentHubBlog?.id }));
    } else {
      dispatch(addHubBlog({ data, history }));
    }
  }

  const {
    errors,
    touched,
    values,
    handleSubmit,
    handleChange,
    setValues,
    clearFormState,
    getFieldProps,
    handleBlur,
  } = useValidator({
    initialValues: {
      company_id: "",
      title: "",
      slug: "",
      description: "",
      tags: "",
    },
    onSubmit,
    validationSchema: Yup.object({
      title: Yup.string().required("Title is Required!"),
      tags: Yup.string().required("Tags is Required!"),
    }),
  });

  useEffect(() => {
    clearFormState();
  }, []);

  useEffect(() => {
    if (id && isEditing) {
      dispatch(getViewData(id));
    }
  }, [id, isEditing]);

  useEffect(() => {
    if (currentHubBlog?.id && isEditing) {
      setValues({
        ...values,
        ...currentHubBlog,
      });
      setTagsAutoComplete(currentHubBlog?.tags?.split(","));
    }
  }, [currentHubBlog, isEditing]);

  return (
    <>
      <FormLayout
        title={"Hub Blog"}
        mode={isEditing ? "Edit" : "Add"}
        pageName={"Hub Blog"}
        parentLink={"Hub Blog"}
      >
        <form
          onSubmit={handleSubmit}
          style={{ maxWidth: 800, margin: "0 auto" }}
        >
          <Box className={flexClasses.flexContainer}>
            <Box className={flexClasses.item}>
              <CustomCard
                title="Blog Details"
                hasSecondaryBtn={isEditing ? true : false}
                secondaryBtnTitle="Media"
                onSecondaryAction={() =>
                  setDocModal({ open: true, data: currentHubBlog })
                }
              >
                <Box className={flexClasses.flexContainer}>
                  <Box className={flexClasses.itemSMD}>
                    {loading ? (
                      <SkeletonLoader type="text" height={40} />
                    ) : (
                      <TextField
                        variant="outlined"
                        size="small"
                        label="Title"
                        placeholder="Title"
                        value={values.title}
                        helperText={touched.title && errors.title}
                        error={!!(touched.title && errors.title)}
                        {...getFieldProps("title")}
                        className={flexClasses.input}
                      />
                    )}
                  </Box>
                  <Box className={flexClasses.itemSMD}>
                    {loading ? (
                      <SkeletonLoader type="text" height={40} />
                    ) : (
                      <Autocomplete
                        multiple
                        options={tagsAutoComplete}
                        freeSolo
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              variant="outlined"
                              label={option}
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                        value={tagsAutoComplete}
                        style={{ marginBottom: "1rem" }}
                        onBlur={handleBlur("tags")}
                        onChange={(event, newValue) => {
                          handleChange("tags")({
                            target: {
                              value:
                                newValue.length == 0
                                  ? ""
                                  : newValue?.map((x) => x),
                            },
                            persist: () => {},
                          });

                          setValues({
                            ...values,
                            tags: newValue,
                          });

                          setTagsAutoComplete(newValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Tags"
                            placeholder="Tags"
                            variant="outlined"
                            id="standard-basic"
                            helperText={touched.tags && errors.tags}
                            error={!!(touched.tags && errors.tags)}
                          />
                        )}
                      />
                    )}
                  </Box>
                </Box>
              </CustomCard>
            </Box>
            <Box className={flexClasses.item}>
              <CustomCard title="Description Detail" mt={true}>
                {loading ? (
                  <SkeletonLoader type="text" height={40} />
                ) : (
                  <TextField
                    multiline={true}
                    minRows={4}
                    variant="outlined"
                    size="small"
                    label="Description"
                    placeholder="Description"
                    value={values.description}
                    helperText={touched.description && errors.description}
                    error={!!(touched.description && errors.description)}
                    {...getFieldProps("description")}
                    className={flexClasses.input}
                  />
                )}
              </CustomCard>
            </Box>
          </Box>
          <div
            className={`${flexClasses.actionWrap} ${flexClasses.marginNone}`}
          >
            <div className={`${flexClasses.actions} `}>
              <SecondaryButton onClick={() => history.goBack()}>
                Cancel
              </SecondaryButton>
              <PrimaryButton type="submit" hideIcon={true} disabled={loading}>
                {isEditing ? "Update" : "Add"}
              </PrimaryButton>
            </div>
          </div>
        </form>
      </FormLayout>
      <Suspense>
        <DocumentModal
          open={docModal.open}
          onClose={() => {
            setDocModal({ open: false });
          }}
          id={currentHubBlog?.id}
          images={currentHubBlog?.images ?? []}
        />
      </Suspense>
    </>
  );
};

export default HubAdd;
